import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { OpenTaskBanner } from '@/components/px/OpenTaskBanner'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { libraryNav } from '@/components/px/nav'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  BREADCRUMB,
  COLUMNS,
  CONTEXT_PILL,
  DENSITY_NOTE,
  KPIS,
  KPI_URGENT_CLASS,
  MODULE_LABEL,
  PAGE_LEDE,
  PAGE_TITLE,
  ROWS,
  SCOUT_PANEL,
  TABLE,
  supportRowClass,
  supportRuleClass,
  supportTone,
  upgradeTone,
} from '@/demo/content/clientsAms'
import type { ContractRow } from '@/demo/content/clientsAms'
import type { Fragment } from '@/demo/content/maintain'
import { cn } from '@/lib/utils'

/**
 * Surface 2.11 — Clients & AMS. The post-go-live annuity: every contract with
 * the version it runs, the upgrade position and the renewal date, so the annual
 * Odoo release lands as a scheduled revenue event rather than a client fire.
 *
 * Two exceptions carry weight, and both are written out as well as coloured:
 * the out-of-support row takes the rose wash and a rose rule, and a renewal
 * inside the 90-day window takes amber ink plus a "in N days" note.
 */
function Sentence({ parts, className }: { parts: readonly Fragment[]; className?: string }) {
  return (
    <span className={className}>
      {parts.map((part) =>
        part.to ? (
          <Link
            key={part.text}
            to={part.to}
            className="text-accent-foreground underline underline-offset-4"
          >
            {part.text}
          </Link>
        ) : (
          <span key={part.text}>{part.text}</span>
        )
      )}
    </span>
  )
}

function ClientCell({ row }: { row: ContractRow }) {
  return (
    <>
      <div className="flex items-center gap-2">
        {row.href ? (
          <Link
            to={row.href}
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            {row.client}
          </Link>
        ) : (
          <span className="font-medium text-foreground">{row.client}</span>
        )}
        {row.note && (
          <Badge variant="outline" className="font-normal text-ink-soft">
            {row.note}
          </Badge>
        )}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">{row.subLine}</div>
    </>
  )
}

export default function ClientsAmsPage() {
  return (
    <AppShell moduleLabel={MODULE_LABEL} breadcrumb={BREADCRUMB} contextPill={CONTEXT_PILL} nav={libraryNav}>
      {/* PX-W6.6 (AC-1.6): this surface renders demo content and had no
          badge. Never hide a surface — badge it. */}
      <div className="mb-3">
        <ConceptBadge />
      </div>
      <header className="max-w-3xl">
        <h1 className="font-serif text-2xl font-semibold text-foreground">{PAGE_TITLE}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{PAGE_LEDE}</p>
      </header>

      <section className="mt-5" aria-label="AMS base">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {KPIS.map((kpi) => (
            <KpiCard
              key={kpi.key}
              label={kpi.label}
              figure={kpi.figure}
              qualifier={kpi.qualifier}
              className={cn(kpi.urgent && KPI_URGENT_CLASS)}
            />
          ))}
        </div>
      </section>

      <section className="mt-8" aria-labelledby="contracts-heading">
        <SectionLabel>{TABLE.label}</SectionLabel>
        <h2 id="contracts-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
          {TABLE.heading}
        </h2>

        <div className="mt-3 rounded-lg border border-border bg-card">
          <Table>
            <TableCaption className="sr-only">{TABLE.caption}</TableCaption>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {COLUMNS.map((column) => (
                  <TableHead
                    key={column.key}
                    scope="col"
                    className={cn(
                      'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
                      column.numeric && 'text-right'
                    )}
                  >
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {ROWS.map((row) => (
                <TableRow key={row.id} className={supportRowClass[row.support]}>
                  <TableCell className={cn('py-3', supportRuleClass[row.support])}>
                    <ClientCell row={row} />
                  </TableCell>
                  <TableCell className="v-figure tabular-nums text-ink-soft">{row.version}</TableCell>
                  <TableCell>
                    <StatusPill tone={supportTone[row.support]}>{row.support}</StatusPill>
                  </TableCell>
                  <TableCell className="v-figure text-right tabular-nums text-foreground">{row.ams}</TableCell>
                  <TableCell className="v-figure text-right tabular-nums text-ink-soft">{row.tickets}</TableCell>
                  <TableCell>
                    <StatusPill tone={upgradeTone[row.upgradeState]}>{row.upgradeLabel}</StatusPill>
                  </TableCell>
                  <TableCell>
                    <div
                      className={cn(
                        'v-figure tabular-nums',
                        row.renewal.soon ? 'font-medium text-review' : 'text-ink-soft'
                      )}
                    >
                      {row.renewal.label}
                    </div>
                    <div
                      className={cn('text-xs', row.renewal.soon ? 'text-review' : 'text-muted-foreground')}
                    >
                      {row.renewal.note}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="mt-8" aria-label={SCOUT_PANEL.sectionLabel}>
        <OpenTaskBanner
          eyebrow={SCOUT_PANEL.eyebrow}
          title={SCOUT_PANEL.title}
          meta={SCOUT_PANEL.meta}
        >
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <StatusPill tone="proposed">{SCOUT_PANEL.quoteChip}</StatusPill>
            <StatusPill tone="neutral">{SCOUT_PANEL.daysChip}</StatusPill>
            <StatusPill tone="review">{SCOUT_PANEL.readyChip}</StatusPill>
          </div>
          <p className="mt-3 max-w-2xl text-sm text-white/80">{SCOUT_PANEL.trust}</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/80">
            <span>{SCOUT_PANEL.handoff}</span>
            <span>{SCOUT_PANEL.agentLine}</span>
            <Link
              to={SCOUT_PANEL.governedHref}
              className="text-white underline underline-offset-4"
            >
              {SCOUT_PANEL.governedLabel}
            </Link>
          </div>
        </OpenTaskBanner>
      </section>

      <DensityNote>
        <Sentence parts={DENSITY_NOTE} />
      </DensityNote>
    </AppShell>
  )
}
